import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BusinessContext } from "../BusinessContext";
import axiosInstance from "../axios";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import DeleteIcon from "@mui/icons-material/Delete";
import AWS from "aws-sdk";
import imageCompression from "browser-image-compression";
import AddIcon from "@mui/icons-material/Add";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

function Gallery() {
  const awsConfig = {
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_KEY,
    endpoint: process.env.REACT_APP_AWS_BASE_URL,
    bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
    publicBaseUrl: process.env.REACT_APP_AWS_PUBLIC_BASE_URL,
  };

  AWS.config.update({
    accessKeyId: awsConfig.accessKeyId,
    secretAccessKey: awsConfig.secretAccessKey,
    endpoint: awsConfig.endpoint,
    s3ForcePathStyle: true,
    signatureVersion: "v4",
  });
  const s3 = new AWS.S3();

  const [images, setImages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const { selectedBusinessId } = useContext(BusinessContext);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const {t}= useTranslation();
  const fetchImages = async () => {
    setPhotoLoading(true);
    try {
      const response = await axiosInstance.post(
        "places.php?method=get_business_images",
        {
          business_id: selectedBusinessId,
        }
      );

      if (response.data.status === "success") {
        setImages(response.data.images); // ← directly use this
      }
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      setPhotoLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, [selectedBusinessId]);

  const openPreview = (index) => {
    setSelectedIndex(index);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error("Error compressing image:", error);
      return file;
    }
  };

  const uploadToS3 = async (file) => {
    const missingConfig = Object.entries({
      accessKeyId: awsConfig.accessKeyId,
      secretAccessKey: awsConfig.secretAccessKey,
      endpoint: awsConfig.endpoint,
      bucket: awsConfig.bucket,
    })
      .filter(([, value]) => !value)
      .map(([key]) => key);

    if (missingConfig.length > 0) {
      throw new Error(
        `Missing local upload configuration: ${missingConfig.join(", ")}`
      );
    }

    const fileName = `${Date.now()}-${file.name}`;
    const params = {
      Bucket: awsConfig.bucket,
      Key: fileName,
      Body: file,
      ContentType: file.type,
      ACL: "public-read",
    };

    try {
      const result = await s3.upload(params).promise();
      const uploadedLocation = result.Location || "";
      const resultBaseUrl = uploadedLocation.endsWith(fileName)
        ? uploadedLocation.slice(0, -fileName.length).replace(/\/$/, "")
        : "";
      const publicBaseUrl = awsConfig.publicBaseUrl || resultBaseUrl;

      if (!publicBaseUrl) {
        throw new Error("Missing public image URL after upload");
      }

      return `${publicBaseUrl.replace(/\/$/, "")}/${encodeURIComponent(fileName)}`;
    } catch (error) {
      console.error("Error uploading to S3:", error);
      throw error;
    }
  };
  const handleUploadImages = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Please upload at least 1 image");
      return;
    }

    if (selectedFiles.length > 5) {
      toast.warn("You can only upload up to 5 images");
      return;
    }

    setUploading(true);
    try {
      const uploadedImageUrls = [];

      for (const file of selectedFiles) {
        const compressedFile = await compressImage(file);
        const imageUrl = await uploadToS3(compressedFile);
        uploadedImageUrls.push(imageUrl);
      }
      const response = await axiosInstance.post("places.php?method=add_business_images", {
        business_id: selectedBusinessId,
        business_images: uploadedImageUrls,
      });
      if (response.data?.status !== "success") {
        throw new Error(response.data?.message || "Images were not saved");
      }
      toast.success("Images uploaded successfully!");
      await fetchImages();
      // Reset form state
      setSelectedFiles([]);
      setUploadModalOpen(false);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.message || "Something went wrong during upload");
    } finally {
      setUploading(false);
    }
  };

  const confirmDeleteImage = (imageId) => {
    setImageToDelete(imageId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteImage = async () => {
    if (!imageToDelete) return;
    try {
      const response = await axiosInstance.post(
        "places.php?method=delete_menu_item",
        {
          id: imageToDelete,
        }
      );

      if (response.data.status === "success") {
        toast.success("Image deleted successfully");
        setImages((prevImages) =>
          prevImages.filter((img) => img.id !== imageToDelete)
        );
      } else {
        toast.error("Failed to delete image");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Error deleting image");
    } finally {
      setIsDeleteModalOpen(false);
      setImageToDelete(null);
    }
  };

  return (
    <Box>
      {photoLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
          p={3}
        >
          <CircularProgress />
        </Box>
      ) : images?.length === 0 ? (
        <Box>
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontSize: { xs: "24px", sm: "24px" },
              fontWeight: "600",
              lineHeight: { xs: "30px", sm: "16px" },
              color: "var(--pg-text)",
              px: "10px",
              pb: { xs: "10px", sm: "30px" },
              pt: "14px",
            }}
          >
             {t("Photostitle")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 10,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Nunito",
                fontSize: "16px",
                fontWeight: "500",
                lineHeight: "16px",
                color: "var(--pg-text-muted)",
                textAlign: "center",
                mb: 2,
              }}
            >
             {t("No Images Available")}
            </Typography>
            <Button
              sx={{
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: "600",
                fontFamily: "Roboto",
                border: "1px solid #0041A8",
                borderRadius: "25px",
                px: "12px",
                py: "3px",
                textTransform: "capitalize",
                color: "#0041A8",
                textAlign: "center",
                alignSelf: "center",
              }}
              onClick={() => setUploadModalOpen(true)}
            >
              <AddIcon />
              {t("Upload Image")}
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontSize: { xs: "24px", sm: "24px" },
                fontWeight: "600",
                lineHeight: { xs: "30px", sm: "16px" },
                color: "var(--pg-text)",
                px: "10px",
                pb: { xs: "10px", sm: "30px" },
                pt: "14px",
              }}
            >
             {t("Photostitle")}
            </Typography>

            <Button
              sx={{
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: "600",
                fontFamily: "Roboto",
                border: "1px solid #0041A8",
                borderRadius: "25px",
                px: "12px",
                py: "3px",
                textTransform: "capitalize",
                color: "#0041A8",
                ml: { xs: "10px", sm: "0px" },
                mb: { xs: "0px", sm: "0px" },
              }}
              onClick={() => setUploadModalOpen(true)}
            >
              <AddIcon />
              {t("Upload Image")}
            </Button>
          </Box>
          {images?.length > 0 && (
            <Box
              sx={{
                mx: "10px",
                columnCount: {
                  xs: 1,
                  sm: 2,
                  md: 3,
                },
                columnGap: 2,
              }}
            >
              {images.map((img, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    breakInside: "avoid",
                    borderRadius: "4px",
                    overflow: "hidden",
                    cursor: "pointer",
                    boxShadow: 1,
                    backgroundColor: "var(--pg-card)",
                    position: "relative",
                  }}
                  onClick={() => openPreview(index)}
                >
                  <img
                    src={img.image}
                    alt={`gallery-img-${index}`}
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      objectFit: "cover",
                    }}
                  />
                  <Box
                    sx={{
                      width: "26px",
                      height: "26px",
                      borderRadius: "50%",
                      backgroundColor: "var(--pg-card)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "absolute",
                      top: "10px",
                      right: "4px",
                    }}
                  >
                    <DeleteIcon
                      sx={{ color: "var(--pg-text)", fontSize: "18px" }}
                      onClick={(event) => {
                        event.stopPropagation();
                        confirmDeleteImage(img.id);
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
      {/* Fullscreen Preview Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleClose}
        sx={{
          backdropFilter: "blur(4px)",
          backgroundColor: "rgba(0,0,0,0.2)",
          "& .MuiDialog-paper": {
            width: "90%",
            maxWidth: 700,
            height: "80vh",
            backgroundColor: "rgba(5, 5, 5, 0.6)",
            borderRadius: 3,
            overflow: "hidden",
            position: "relative",
          },
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 10,
            backgroundColor: "",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#d32f2f",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Image Counter */}
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 10,
            backgroundColor: "rgba(92, 89, 89, 0.6)",
            color: "#fff",
            px: 1.5,
            py: 0.5,
            borderRadius: "4px",
            fontSize: "14px",
          }}
        >
          {selectedIndex + 1} / {images.length}
        </Box>

        {/* Swiper Slider */}
        <DialogContent sx={{ height: "", p: 0 }}>
          <Swiper
            modules={[Navigation]}
            navigation={{
              nextEl: ".custom-next",
              prevEl: ".custom-prev",
            }}
            initialSlide={selectedIndex}
            onSlideChange={(swiper) => {
              setSelectedIndex(swiper.activeIndex);
              setActiveIndex(swiper.activeIndex);
            }}
            style={{ height: "100%" }}
          >
            {/* Slides */}

            {images.map((img, idx) => (
              <SwiperSlide key={idx}>
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(92, 89, 89, 0.6)",
                  }}
                >
                  <img
                    src={img.image}
                    alt={`preview-${idx}`}
                    style={{
                      maxHeight: "90%",
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </SwiperSlide>
            ))}
            <IconButton
              className="custom-prev"
              sx={{
                position: "absolute",
                top: "50%",
                left: 16,
                zIndex: 10,
                transform: "translateY(-50%)",
                color: "white",
                opacity: activeIndex === 0 ? 0.3 : 1,
                pointerEvents: activeIndex === 0 ? "none" : "auto",
                transition: "opacity 0.3s",
              }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>

            <IconButton
              className="custom-next"
              sx={{
                position: "absolute",
                top: "50%",
                right: 16,
                zIndex: 10,
                transform: "translateY(-50%)",
                color: "white",
                opacity: activeIndex === images.length - 1 ? 0.3 : 1,
                pointerEvents:
                  activeIndex === images.length - 1 ? "none" : "auto",
                transition: "opacity 0.3s",
              }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          </Swiper>
        </DialogContent>
      </Dialog>
      <Dialog
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setSelectedFiles([]);
        }}
      >
        <DialogContent
          sx={{ p: 4, width: { xs: "100%", sm: 500 }, position: "relative" }}
        >
          {/* Close Button */}
          <IconButton
            onClick={() => {
              setUploadModalOpen(false);
              setSelectedFiles([]);
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#000",
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: "20px", mb: 2 }}
          >
            {t("Upload Images")}
          </Typography>

          {/* Upload Box */}
          <Box
            onClick={() =>
              document.getElementById("image-upload-input").click()
            }
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              mb: 3,
              "&:hover": {
                borderColor: "#0041A8",
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: "16px", color: "#666", fontWeight: 500 }}
            >
              +{t("Upload Image")}
            </Typography>
            <input
              type="file"
              id="image-upload-input"
              multiple
              accept="image/*"
              hidden
              onChange={(e) => {
                const newFiles = Array.from(e.target.files);
                const totalFiles = selectedFiles.length + newFiles.length;

                if (totalFiles > 5) {
                  toast.info(
                    "🚫 You can only upload up to 5 images at a time",
                    {
                      position: "top-center",
                      autoClose: 3000,
                      hideProgressBar: false,
                      closeOnClick: true,
                      pauseOnHover: true,
                      draggable: true,
                      progress: undefined,
                      theme: "colored",
                    }
                  );

                  // Only add up to 5 files in total
                  const allowedFiles = newFiles.slice(
                    0,
                    5 - selectedFiles.length
                  );
                  setSelectedFiles((prevFiles) => [
                    ...prevFiles,
                    ...allowedFiles,
                  ]);
                } else {
                  setSelectedFiles((prevFiles) => [...prevFiles, ...newFiles]);
                }

                // Reset input to allow same file selection again
                e.target.value = null;
              }}
            />
          </Box>

          {/* Image Previews */}
          <Box display="flex" flexWrap="wrap" gap={2}>
            {selectedFiles.map((file, index) => (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: 1,
                }}
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt={`preview-${index}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    backgroundColor: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "red",
                    },
                  }}
                  onClick={() => {
                    const newFiles = [...selectedFiles];
                    newFiles.splice(index, 1);
                    setSelectedFiles(newFiles);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>

          {/* Upload Button */}
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
            disabled={uploading}
            onClick={handleUploadImages}
            fullWidth
          >
            {uploading ?  t("Uploading..."): t("Upload")}
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <DialogContent
          sx={{ p: 4, width: { xs: "100%", sm: 500 }, position: "relative" }}
        >
          {/* Close Button */}
          <IconButton
            onClick={() => setIsDeleteModalOpen(false)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#000",
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              fontSize: "18px",
              fontFamily: "Roboto",
              fontWeight: "600",
              lineHeight: "100%",
              mb: 2,
            }}
          >
             {t("Delete Image")}
          </Typography>
          <Typography variant="h6" sx={{ fontSize: "16px", mb: 2 }}>
          {t("Are you sure you want to delete this image?")}
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteImage}
            >
              {t("Delete")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <ToastContainer position="top-center" autoClose={2000} />
    </Box>
  );
}

export default Gallery;
